import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useMemo,
  PropsWithChildren,
  ElementType,
  ReactElement,
} from 'react';
import styled, {
  createGlobalStyle, css, FlattenInterpolation, ThemeProps,
} from 'styled-components';
import _ from 'lodash';
import moment from 'moment';
import DataTable, { TableStyles, TableProps as DataTableProps, TableColumn } from './DataTable';
import { CloseIcon } from '../Icons';
import {
  FormConfigurationProvider, LinkButton, TextFieldProps, TextInput,
} from '../form';
import { ThemeType, useStyledTheme } from '../../styling';
import { TableMenuActions } from './TableMenuActions';
import { EmptyData, EmptySearchData } from './EmptyData';
import { formatDateTime } from '../../../utils';
import { TablePagination } from './TablePagination';
import { Flex } from '../Flex';
import { LoadingSpinner } from '../Loader';

const GlobalTableStyle = createGlobalStyle<{ theme: ThemeType }>`
  .rdt_TableCell {
    padding-left: ${({ theme }) => theme.padding[2]};
    padding-right: ${({ theme }) => theme.padding[2]};
    justify-content: start;
  }
  .rdt_TableRow {
    background: ${({ theme }) => theme.colours.primary[11]};
  }
  .rdt_Table {
    row-gap: ${({ theme }) => theme.padding[1]};
  }
  .rdt_TableBody {
    row-gap: ${({ theme }) => theme.padding[1]};
  }
  .rdt_TableCell > div {
    position: relative;
  }
  .rdt_TableCell > div > div{
    position: absolute;
  }
`;

export const TableIcon = styled.i` ${({ theme }) => `
  font-size: ${theme.fontSize[4]};
`}`;

export const TableSelectCss = css<{ hasError?: boolean }>` ${({ theme, hasError }) => `
  font-size: ${theme.fontSize[1]};
  border: ${theme.borderWidth[1]} solid ${hasError ? theme.textDanger : theme.colours.primary[6]};
  background: none !important;
  height: ${theme.padding[7]};
  padding: 0 ${theme.padding[2]};
`}`;

export const ActionIconContainer = styled.div` ${({ theme }) => `
  display: flex;
  align-items: center;
  width:10vw;
  gap: ${theme.padding[4]};
`}`;

export const IconLink = styled.a<{ disabled?: boolean }>`${({ theme, disabled }) => `
  padding-right: ${theme.padding[1]};
  padding-left: ${theme.padding[1]};
  pointer-events: ${disabled ? 'none' : 'unset'};
`}`;

const TableContainer = styled.div`
  display:flex;
  flex-direction:column;
  width:100%;
  box-sizing: border-box;
`;

const TableSearchContainer = styled.div`
  width:20vw;
  min-width:300px;
`;

const SearchIconIcon = styled(TableIcon)`${({ theme }) => `
  color:${theme.textNeutral};
`}`;

const SearchIconContainer = styled(SearchIconIcon)`${({ theme }) => `
  font-size: ${theme.fontSize[2]};
`}`;

const CloseSearchIconContainer = styled(Flex)`${({ theme }) => `
  background: ${theme.colours.neutral[3]};
  width: ${theme.fontSize[2]};
  height: ${theme.fontSize[2]};
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`}`;

const SearchIcon = (
  <SearchIconContainer className="mi-search" />
);

const HeaderContainer = styled.div`${({ theme }) => `
  display:flex;
  flex-direction:row;
  justify-content: start;
  padding-bottom: ${theme.padding[4]};
  gap:${theme.padding[2]};
`}`;

const ReactDataTableContainer = styled.div<{ containerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  div {
    table-layout: fixed;
  };
  
  >div {
    overflow: visible;
  };

  ${({ containerCss }) => containerCss};
`;

const Icon = styled.i` ${({ theme }) => `
  font-size:${theme.fontSize[4]};
`}`;

const TableLoadingSpinnerContainer = styled(Flex)` ${({ theme }) => `
  padding:${theme.padding[8]};
  min-height:50vh;
`}`;

const searchStyles = css`
  height: 32px;
  border-radius: 8px;
`.toString();

export const SectionIconButton = styled(LinkButton)`${({ theme }) => `
  padding: 0;
  margin: 0;
  height: auto;
  font-size: ${theme.fontSize[3]};
  min-width: auto;

  :hover:not([disabled]) {
    transform: scale(1.2);
    cursor: pointer;
  }
`}`;

export const FlexTableCell = styled(Flex) <{ justify?: 'space-between' | 'center' | 'flex-end' }>`
  justify-content: ${({ justify = 'space-between' }) => justify};
  width: 100%;
`;

export const RowContext = createContext<any>(null);

function DefaultExpandableCell({ children }: PropsWithChildren) {
  return <>{children}</>;
}

function TableLoadingSpinner() {
  return (
    <TableLoadingSpinnerContainer>
      <LoadingSpinner />
    </TableLoadingSpinnerContainer>
  );
}

const MenuCell = (menu: ReactNode) => function (row: any, index: number) {
  return (
    <RowContext.Provider value={row}>
      <TableMenuActions
        id={`table-menu-${index}`}
        disabled={false}
      >
        {menu}
      </TableMenuActions>
    </RowContext.Provider>
  );
};

export function TableSearchInput({
  onChange,
  placeholder = 'Search',
  value,
  ...rest
}: TextFieldProps) {
  const theme = useStyledTheme();
  const CloseSearch = (
    <CloseSearchIconContainer onClick={() => onChange('')}>
      <CloseIcon width={theme.padding[2]} height={theme.padding[2]} fill={theme.colours.neutral[10]} />
    </CloseSearchIconContainer>
  );

  return (
    <TableSearchContainer>
      <TextInput
        {...rest}
        styles={searchStyles}
        onChange={onChange}
        placeholder={placeholder}
        prefix={SearchIcon}
        value={value}
        suffix={value !== '' ? CloseSearch : <></>}
      />
    </TableSearchContainer>
  );
}

export type SubHeaderContainerProps = PropsWithChildren & {
  searchComponent?: ReactNode;
};

export function DefaultSubHeaderContainer({ children, searchComponent }: SubHeaderContainerProps) {
  return (
    <HeaderContainer>
      {searchComponent}
      {children}
    </HeaderContainer>
  );
}

export interface TableProps<T> extends DataTableProps<T> {
  id?: string,
  showDisabledExpandableIcon?: boolean,
  styles?: TableStyles,
  SubHeaderContainer?: (props: SubHeaderContainerProps) => ReactElement,
  menu?: ReactNode,
  isFiltered?: boolean,
  stickyHeaderOffset?: string,
  ExpandableCell?: ElementType,
  expandableColumnOptions?: Partial<TableColumn<any>>,
  showSearch?: boolean
  searchInputProps?: Partial<TextFieldProps> & {
    customFilter?: (data: T) => boolean;
  }
  containerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  forcedExpandRows?: string[],
}

export const DateSort = <T, >(property: keyof T) => (rowA: T, rowB: T) => {
  const format = 'YYYYMMDDHHmmss';
  const formattedRowA = formatDateTime(rowA[property] as moment.MomentInput, format);
  const formattedRowB = formatDateTime(rowB[property] as moment.MomentInput, format);

  if (formattedRowA < formattedRowB) {
    return -1;
  }
  if (formattedRowA > formattedRowB) {
    return 1;
  }
  return 0;
};

export const TextSort = <T, >(property: keyof T) => (rowA: T, rowB: T) => {
  const a = rowA[property] ? String(rowA[property]).trim() : '';
  const b = rowB[property] ? String(rowB[property]).trim() : '';
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

export function Table<T extends { id?: string }>({
  id = '',
  data,
  columns,
  expandableRowsComponent,
  expandableRowsComponentProps = {},
  showDisabledExpandableIcon = false,
  styles = {},
  subHeaderComponent,
  SubHeaderContainer = DefaultSubHeaderContainer,
  noDataComponent = (<EmptyData />),
  menu,
  isFiltered = false,
  expandableRowDisabled = () => false,
  stickyHeaderOffset = '0',
  ExpandableCell = DefaultExpandableCell,
  expandableColumnOptions = {},
  showSearch = true,
  searchInputProps,
  containerCss,
  forcedExpandRows = [],
  persistTableHead = true,
  ...props
}: TableProps<T>) {
  const theme = useStyledTheme();

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const customStyles = {
    rows: {
      style: {
        color: theme.colours.neutral[2],
      },
    },
    head: {
      style: {
        color: theme.colours.primary[1],
        fontSize: theme.fontSize[1],
        position: 'sticky',
        top: stickyHeaderOffset,
        zIndex: 1,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.colours.primary[10],
        borderBottom: 'none',
        minHeight: theme.collapseHeaderHeight,
      },
    },
    headCells: {
      style: {
        padding: theme.padding[2],
        display: 'flex',
        justifyContent: 'start',
        fontWeight: theme.fontWeight.bold,
        fontSize: theme.fontSize[1],
        'div': {
          whiteSpace: 'normal',
        },
      },
      rows: {
        style: {
          color: theme.textPrimary,
          fontSize: theme.fontSize[1],
          minHeight: '35px',
          zIndex: '99999',
        },
      },
      cells: {
        style: {
          background: theme.colours.primary[11],
          color: theme.colours.neutral[2],
          justifyContent: 'start',
        },
      },
      pagination: {
        style: {
          borderBottomLeftRadius: theme.borderRadius[1],
          borderBottomRightRadius: theme.borderRadius[1],
          borderTopWidth: 0,
        },
      },
    },
  };
  const [filterText, setFilterText] = useState<string>('');
  const [filteredData, setFilteredData] = useState<T[]>([]);

  useEffect(() => {
    if (filterText === '' || filterText === undefined) {
      setFilteredData(data);
    } else {
      setFilteredData(data.filter(
        (dataValue: T) => Object.values<unknown>(dataValue).some(
          (value) => {
            let valueString = '';
            if (Array.isArray(value)) valueString = JSON.stringify(value);
            if (typeof value === 'string') valueString = value;
            return valueString.toLowerCase().includes(filterText.toLowerCase());
          },
        ),
      ));
    }
  }, [filterText, data]);

  const expandedData = filteredData.map((current) => ({
    ...current,
    expanded: !expandableRowDisabled?.(current) && !!current.id && !!expandedRows?.[current.id],
  }));

  useEffect(() => {
    if (!forcedExpandRows || !forcedExpandRows?.length) return;
    setExpandedRows((prev) => ({ ...forcedExpandRows.reduce((accum, rowId) => ({ ...accum, [rowId]: true }), prev) }));
  }, [forcedExpandRows]);

  const ExpandableIconCell = (row: T) => useMemo(
    () => (
      <ExpandableCell row={row}>
        {(!expandableRowDisabled?.(row) || showDisabledExpandableIcon) && (
          <FormConfigurationProvider isReadOnlyInput={false}>
            <SectionIconButton
              id="SectionIconButton"
              disabled={expandableRowDisabled?.(row)}
              onClick={() => setExpandedRows((prev) => {
                if (!row.id) return prev;
                return {
                  ...prev,
                  [row.id || '']: !expandedRows?.[row.id || ''],
                };
              })}
            >
              <Icon
                className={
                  (!expandableRowDisabled?.(row) && row.id && expandedRows?.[row.id])
                    ? 'mi-chevron-up'
                    : 'mi-chevron-down'
                }
              />
            </SectionIconButton>
          </FormConfigurationProvider>
        )}
      </ExpandableCell>
    ),
    [expandedData, ExpandableCell],
  );

  const tableColumns = [
    ...columns,
    ...(menu ? [{
      cell: MenuCell(menu),
      style: {
        justifyContent: 'right',
      },
    }] : []),
    ...(expandableRowsComponent ? [{
      cell: ExpandableIconCell,
      width: '35px',
      height: '35px',
      style: {
        justifyContent: 'right',
      },
      ...expandableColumnOptions,
    }] : []),
  ];

  return (
    <TableContainer id={id}>
      {(showSearch || subHeaderComponent) && (
        <SubHeaderContainer
          searchComponent={showSearch && ((
            <TableSearchInput
              {...searchInputProps}
              id="search"
              styles={searchStyles}
              onChange={(value) => setFilterText(value)}
              value={filterText}
            />
          ))}
        >
          {subHeaderComponent}
        </SubHeaderContainer>
      )}
      <ReactDataTableContainer containerCss={containerCss}>
        <GlobalTableStyle />
        <DataTable
          {...props}
          columns={tableColumns}
          data={expandedData}
          customStyles={_.merge(customStyles, styles)}
          progressComponent={<TableLoadingSpinner />}
          responsive
          noDataComponent={!!filterText || isFiltered ? <EmptySearchData /> : noDataComponent}
          expandableRows={!!expandableRowsComponent}
          expandableRowsComponent={expandableRowsComponent}
          expandableRowsComponentProps={expandableRowsComponentProps}
          expandableRowsHideExpander
          expandableRowDisabled={expandableRowDisabled}
          expandableRowExpanded={(item: T) => !expandableRowDisabled?.(item) && !!item.id && expandedRows?.[item.id]}
          paginationComponent={TablePagination as any}
          persistTableHead={persistTableHead}
        />
      </ReactDataTableContainer>
    </TableContainer>
  );
}
